import { useEffect, useMemo, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Link } from "react-router-dom"

import { api, monthName } from "../api"
import { LoadingCard, Page } from "../shared"
import { fa, STATE_LABEL, type AllocationState, type TasksResponse } from "../process"
import { cn } from "@/lib/utils"
import { SEASONS, YearPicker } from "@/components/period-picker"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

type Summary = { id: number; jalaliYear: number; jalaliMonth: number; status: string; lines: Array<{ percentage: string }> }

const TONE: Record<AllocationState, string> = {
  missing: "border-dashed text-muted-foreground",
  draft: "border-amber-300 bg-amber-50",
  rejected: "border-destructive/50 bg-destructive/5",
  submitted: "border-blue-300 bg-blue-50",
  div_approved: "border-blue-300 bg-blue-50",
  approved: "border-emerald-300 bg-emerald-50",
}

/** The person's monthly splits: pick a year, see its 12 months as tiles grouped by season. */
export function AllocationHistoryPage() {
  const allocations = useQuery({ queryKey: ["allocations"], queryFn: () => api<Summary[]>("/allocations") })
  const tasks = useQuery({ queryKey: ["process-tasks"], queryFn: () => api<TasksResponse>("/process/tasks") })
  const period = tasks.data?.cycle.period
  const [year, setYear] = useState<number | null>(null)
  useEffect(() => { if (period && year === null) setYear(period.year) }, [period, year])

  const byMonth = useMemo(() => new Map((allocations.data ?? []).map((item) => [`${item.jalaliYear}-${item.jalaliMonth}`, item])), [allocations.data])
  const years = useMemo(() => {
    const set = new Set((allocations.data ?? []).map((item) => item.jalaliYear))
    if (period) set.add(period.year)
    return [...set].sort((a, b) => a - b)
  }, [allocations.data, period])

  if (!allocations.data || !period || year === null) return <Page title="نسبت کاری من"><LoadingCard /></Page>
  const ord = (y: number, m: number) => y * 12 + m

  return (
    <Page title="نسبت کاری من" description="نسبت کاری هر ماه و وضعیت تأیید آن. روی هر ماه بزنید تا جزئیات را ببینید.">
      <div className="flex justify-center"><YearPicker years={years} value={year} onChange={setYear} /></div>

      <div className="grid gap-4 md:grid-cols-2">
        {SEASONS.map((season) => (
          <Card key={season.name}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base"><season.icon className={cn("size-4", season.tone)} /> {season.name} {fa(year)}</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-3 gap-3">
              {season.months.map((month) => {
                const item = byMonth.get(`${year}-${month}`)
                const isCurrent = year === period.year && month === period.month
                const future = ord(year, month) > ord(period.year, period.month)
                const state = (item?.status ?? "missing") as AllocationState
                const total = item ? item.lines.reduce((sum, line) => sum + Number(line.percentage) * 100, 0) : 0
                const to = item ? `/allocations/${item.id}` : isCurrent ? "/allocations/current" : null
                const body = (
                  <>
                    <span className="text-sm font-semibold">{monthName(month)}</span>
                    <span className="text-[11px] leading-4">{future ? "هنوز نرسیده" : isCurrent && !item ? "ثبت کنید" : STATE_LABEL[state]}</span>
                    {item && item.lines.length > 0 && <span className="text-[11px] text-muted-foreground tabular-nums">{total.toLocaleString("fa-IR", { maximumFractionDigits: 1 })}٪</span>}
                  </>
                )
                const classes = cn(
                  "flex aspect-square flex-col items-center justify-center gap-1 rounded-xl border p-2 text-center transition-colors",
                  future ? "border-dashed opacity-40" : TONE[state],
                  isCurrent && "ring-2 ring-primary ring-offset-2",
                  to && "hover:border-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
                )
                return to && !future
                  ? <Link key={month} to={to} className={classes} aria-label={`${monthName(month)} ${year}`}>{body}</Link>
                  : <div key={month} className={classes}>{body}</div>
              })}
            </CardContent>
          </Card>
        ))}
      </div>
    </Page>
  )
}
